import { useMutation } from '@tanstack/react-query';
import { findIndex } from 'lodash';
import { produce } from 'immer';
import { placeAssetInAudioWidget } from '../../helpers/api/guidelinesApi';
import { AudioWidget, Page, WIDGET_TYPE } from '../../types';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import { GUIDE_KEYS } from '../../queries/guides';
import { queryClient } from '../../constants/app';
import { useGuideStore } from '../../stores/guideStore';

export const useAudioWidgetMutations = (
  sectionId: string,
  columnIndex: number,
  widgetId?: string,
) => {
  const activeGuideId = useGuideStore(s => s.activeGuideId) as string;
  const activePageId = useGuideStore(s => s.activePageId) as string;
  const activeChapterId = useGuideStore(s => s.activeChapterId) as string;
  const activeGuideVersion = useGuideStore(s => s.activeGuideVersion) as number;

  return {
    updateAudioWidget: useMutation(
      (options: {
        assetId: string;
        assetName: string;
        audioUrl: string;
        detailUrl: string;
      }) => {
        let wgtId = widgetId;

        if (!wgtId) {
          wgtId = sessionStorage.getItem('newWidgetId') as string;
        }

        return placeAssetInAudioWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          wgtId,
          WIDGET_TYPE.AUDIO,
          {
            columnIndex,
            widgetAudioPayload: {
              assetId: options.assetId,
              assetName: options.assetName,
              audioUrl: options.audioUrl,
              detailUrl: options.detailUrl,
            },
          },
          activeGuideVersion,
        );
      },
      {
        onMutate: variables => {
          let wgtId = widgetId;

          if (!wgtId) {
            wgtId = sessionStorage.getItem('newWidgetId') as string;
          }

          updateGuideVersion(activeGuideId);
          optimisticallyUpdateAudioWidget(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            wgtId,
            variables,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
  };
};

const optimisticallyUpdateAudioWidget = (
  guideId: string,
  pageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  variables: {
    assetId: string;
    assetName: string;
    audioUrl: string;
    detailUrl: string;
  },
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, guideId, pageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );
      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );
      const newWidget: AudioWidget = {
        ...old.sections[sectionIndex].columns[columnIndex].widgets[widgetIndex],
        audioUrl: variables.audioUrl,
        assetName: variables.assetName,
        detailUrl: variables.detailUrl,
        assetId: variables.assetId,
      };

      if (old) {
        return produce(old, draft => {
          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = newWidget;
        });
      }

      return old;
    },
  );
};
